import { Timestamp } from 'firebase/firestore';
import type {
  PayrollCompPeriod,
  PayrollPosition,
  PayrollScheduledRaise,
  PayrollSettings,
} from '../types/finances';
import { DEFAULT_PAYROLL_SETTINGS } from '../types/finances';

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

export function mapScheduledRaiseFromFirestore(raw: Record<string, unknown>): PayrollScheduledRaise | null {
  const id = typeof raw.id === 'string' ? raw.id : '';
  const effectiveDate = toDate(raw.effectiveDate);
  const kind = raw.kind === 'percent' || raw.kind === 'dollar' || raw.kind === 'set' ? raw.kind : null;
  const amount = typeof raw.amount === 'number' ? raw.amount : NaN;
  const createdAt = toDate(raw.createdAt);
  if (!id || !effectiveDate || !kind || !Number.isFinite(amount) || !createdAt) return null;
  return {
    id,
    effectiveDate,
    kind,
    amount,
    label: typeof raw.label === 'string' ? raw.label : undefined,
    createdAt,
  };
}

export function mapCompPeriodFromFirestore(raw: Record<string, unknown>): PayrollCompPeriod | null {
  const id = typeof raw.id === 'string' ? raw.id : '';
  const effectiveDate = toDate(raw.effectiveDate);
  const annualSalary = typeof raw.annualSalary === 'number' ? raw.annualSalary : NaN;
  const source =
    raw.source === 'hire' || raw.source === 'manual' || raw.source === 'cola' || raw.source === 'raise'
      ? raw.source
      : 'manual';
  if (!id || !effectiveDate || !Number.isFinite(annualSalary)) return null;
  return {
    id,
    effectiveDate,
    endDate: toDate(raw.endDate),
    annualSalary,
    hourlyRate: typeof raw.hourlyRate === 'number' ? raw.hourlyRate : null,
    hoursPerWeek: typeof raw.hoursPerWeek === 'number' ? raw.hoursPerWeek : null,
    label: typeof raw.label === 'string' ? raw.label : undefined,
    source,
  };
}

export function serializeScheduledRaise(r: PayrollScheduledRaise): Record<string, unknown> {
  return {
    id: r.id,
    effectiveDate: Timestamp.fromDate(new Date(r.effectiveDate)),
    kind: r.kind,
    amount: r.amount,
    label: r.label ?? null,
    createdAt: Timestamp.fromDate(new Date(r.createdAt)),
  };
}

export function serializeCompPeriod(p: PayrollCompPeriod): Record<string, unknown> {
  return {
    id: p.id,
    effectiveDate: Timestamp.fromDate(new Date(p.effectiveDate)),
    endDate: p.endDate ? Timestamp.fromDate(new Date(p.endDate)) : null,
    annualSalary: p.annualSalary,
    hourlyRate: p.hourlyRate ?? null,
    hoursPerWeek: p.hoursPerWeek ?? null,
    label: p.label ?? null,
    source: p.source,
  };
}

export function serializeScheduledRaises(raises: PayrollScheduledRaise[] | undefined): Record<string, unknown>[] {
  return (raises ?? []).map(serializeScheduledRaise);
}

export function serializeCompPeriods(periods: PayrollCompPeriod[] | undefined): Record<string, unknown>[] {
  return (periods ?? []).map(serializeCompPeriod);
}

export function parsePositionCompFields(data: Record<string, unknown>): {
  scheduledRaises: PayrollScheduledRaise[];
  compPeriods: PayrollCompPeriod[];
} {
  const scheduledRaises = Array.isArray(data.scheduledRaises)
    ? data.scheduledRaises
        .map((row: Record<string, unknown>) => mapScheduledRaiseFromFirestore(row))
        .filter((r): r is PayrollScheduledRaise => r != null)
    : [];
  const compPeriods = Array.isArray(data.compPeriods)
    ? data.compPeriods
        .map((row: Record<string, unknown>) => mapCompPeriodFromFirestore(row))
        .filter((p): p is PayrollCompPeriod => p != null)
    : [];
  return { scheduledRaises, compPeriods };
}

export function mapPayrollPositionFromFirestore(id: string, data: Record<string, unknown>): PayrollPosition {
  const { scheduledRaises, compPeriods } = parsePositionCompFields(data);
  const hasLegacyBurden =
    data.benefitsAndTaxRate !== undefined &&
    data.benefitsAndTaxRate !== null &&
    data.healthReimbursementMonthly === undefined;
  const employerTaxRateOverride = hasLegacyBurden
    ? Number(data.benefitsAndTaxRate)
    : ((data.employerTaxRateOverride as number | null | undefined) ?? null);
  return {
    id,
    title: typeof data.title === 'string' ? data.title : '',
    department: (data.department as PayrollPosition['department']) || 'other',
    status: (data.status as PayrollPosition['status']) || 'filled',
    payType: data.payType === 'hourly' ? 'hourly' : 'salary',
    annualSalary: typeof data.annualSalary === 'number' ? data.annualSalary : 0,
    hourlyRate: typeof data.hourlyRate === 'number' ? data.hourlyRate : null,
    hoursPerWeek: typeof data.hoursPerWeek === 'number' ? data.hoursPerWeek : null,
    healthReimbursementMonthly: typeof data.healthReimbursementMonthly === 'number' ? data.healthReimbursementMonthly : 0,
    ptoDaysPerYear: typeof data.ptoDaysPerYear === 'number' ? data.ptoDaysPerYear : 0,
    monthlyEmployeeExtras: typeof data.monthlyEmployeeExtras === 'number' ? data.monthlyEmployeeExtras : 0,
    monthlyEmployeeExtrasNote: typeof data.monthlyEmployeeExtrasNote === 'string' ? data.monthlyEmployeeExtrasNote : undefined,
    sepIraRateOverride: typeof data.sepIraRateOverride === 'number' ? data.sepIraRateOverride : null,
    employerTaxRateOverride,
    benefitsAndTaxRate: hasLegacyBurden ? Number(data.benefitsAndTaxRate) : null,
    startDate: toDate(data.startDate) || new Date(),
    endDate: toDate(data.endDate),
    crmUserId: typeof data.crmUserId === 'string' ? data.crmUserId : null,
    workState: typeof data.workState === 'string' ? data.workState : null,
    notes: typeof data.notes === 'string' ? data.notes : undefined,
    isActive: data.isActive !== false,
    scheduledRaises,
    compPeriods,
    createdAt: toDate(data.createdAt) || new Date(),
    updatedAt: toDate(data.updatedAt) || new Date(),
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
  };
}

export function mapPayrollSettingsFromFirestore(data: Record<string, unknown> | undefined): PayrollSettings {
  if (!data) return { ...DEFAULT_PAYROLL_SETTINGS };
  const updatedAt =
    data.updatedAt instanceof Date
      ? data.updatedAt
      : data.updatedAt && typeof (data.updatedAt as { toDate?: () => Date }).toDate === 'function'
        ? (data.updatedAt as { toDate: () => Date }).toDate()
        : null;
  return {
    sepIraRate: typeof data.sepIraRate === 'number' ? data.sepIraRate : DEFAULT_PAYROLL_SETTINGS.sepIraRate,
    employerTaxRate:
      typeof data.employerTaxRate === 'number' ? data.employerTaxRate : DEFAULT_PAYROLL_SETTINGS.employerTaxRate,
    defaultPtoDaysPerYear:
      typeof data.defaultPtoDaysPerYear === 'number'
        ? data.defaultPtoDaysPerYear
        : DEFAULT_PAYROLL_SETTINGS.defaultPtoDaysPerYear,
    defaultHealthReimbursementMonthly:
      typeof data.defaultHealthReimbursementMonthly === 'number'
        ? data.defaultHealthReimbursementMonthly
        : 0,
    defaultMonthlyEmployeeExtras:
      typeof data.defaultMonthlyEmployeeExtras === 'number' ? data.defaultMonthlyEmployeeExtras : 0,
    defaultWorkState:
      typeof data.defaultWorkState === 'string' && data.defaultWorkState.trim()
        ? data.defaultWorkState.trim().toUpperCase()
        : DEFAULT_PAYROLL_SETTINGS.defaultWorkState,
    updatedAt,
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : undefined,
  };
}

export function positionCompFirestorePatch(position: Partial<PayrollPosition>): Record<string, unknown> {
  return {
    scheduledRaises: serializeScheduledRaises(position.scheduledRaises),
    compPeriods: serializeCompPeriods(position.compPeriods),
    annualSalary: position.annualSalary ?? 0,
    hourlyRate: position.payType === 'hourly' ? (position.hourlyRate ?? null) : null,
    hoursPerWeek: position.payType === 'hourly' ? (position.hoursPerWeek ?? null) : null,
  };
}
